import React from "react";
import { AI_DISCLAIMER_TEXT } from "../utils/aiDisclaimer";

/**
 * <AIImageWithDisclaimer> wraps any AI-generated <img> with a readable
 * disclaimer band at the bottom. Used across DoozyGo Magic + AI Party
 * Planner so the attribution travels with every rendered AI image —
 * including galleries, lightboxes, saved items and admin viewers.
 *
 * It is intentionally *not* applied to the customer's original uploaded
 * photo (the "BEFORE" image in the Magic slider).
 *
 * Props:
 *   - All standard <img> props (src, alt, className, draggable, style, …)
 *   - wrapperClassName?: extra classes on the outer <div>
 *   - overlayPosition?: 'absolute' (default — rides on top of the img) |
 *                       'inside'   (static-stacks under the image; use
 *                                   when parent clips with overflow-hidden)
 *   - compact?: boolean (smaller text — for grid thumbnails)
 */
export const AIImageWithDisclaimer = React.forwardRef(function AIImageWithDisclaimer(
  { wrapperClassName = "", compact = false, style, children, ...imgProps },
  ref,
) {
  const textSize = compact ? "text-[8px] sm:text-[9px]" : "text-[9px] sm:text-[11px]";
  const padding = compact ? "px-1.5 py-0.5" : "px-2 py-1";
  return (
    <div className={`relative block ${wrapperClassName}`} style={style} data-ai-disclaimer-wrapper>
      <img ref={ref} {...imgProps} />
      <div
        className={`pointer-events-none absolute inset-x-0 bottom-0 ${padding} ${textSize} font-medium text-white text-center leading-tight bg-black/55 backdrop-blur-[2px]`}
        data-testid="ai-image-disclaimer"
      >
        {AI_DISCLAIMER_TEXT}
      </div>
      {children}
    </div>
  );
});

export default AIImageWithDisclaimer;
